import React from "react";
import { IoCloseOutline } from "react-icons/io5";
import { motion } from "framer-motion";
import { FaPrint } from "react-icons/fa";

const EligibilityModal = ({ setModal, data }) => {
  const eligibilityStatus = data.eligibility_result || "Unknown";
  const isDisqualified = eligibilityStatus
    .toLowerCase()
    .includes("disqualified");

  const handlePrint = () => {
    const modalContent = document.querySelector(".eligibility-modal-content");

    if (!modalContent) return;

    const printWindow = window.open("", "_blank", "width=1000,height=800");
    const styles = Array.from(document.styleSheets)
      .map((styleSheet) => {
        try {
          return Array.from(styleSheet.cssRules)
            .map((rule) => rule.cssText)
            .join("");
        } catch (e) {
          return "";
        }
      })
      .join("");

    printWindow?.document.write(`
    <html>
      <head>
        <title>Print Eligibility Result</title>
        <style>${styles}</style>
      </head>
      <body class="bg-white">
        <div class="p-6">${modalContent.innerHTML}</div>
      </body>
    </html>
  `);

    printWindow?.document.close();
    printWindow?.focus();

    setTimeout(() => {
      printWindow?.print();
      printWindow?.close();
    }, 500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm print:relative print:bg-transparent print:backdrop-blur-none">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="eligibility-modal-content relative w-full max-w-6xl max-h-[95vh] overflow-y-auto bg-white shadow-xl rounded-xl p-4 print:max-h-none print:overflow-visible"
      >
        <div className="flex items-center justify-between sticky top-0 bg-white pb-3 border-b border-gray-200 print:static">
          <div className="flex items-center space-x-2">
            <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center">
              <span className="text-blue-600 font-bold text-xs">EC</span>
            </div>
            <p className="text-base font-semibold text-gray-800">
              Eligibility Check Results
            </p>
          </div>
          <IoCloseOutline
            onClick={() => setModal(false)}
            className="text-xl text-gray-600 cursor-pointer hover:text-gray-900 transition print:hidden"
          />
        </div>

        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-3 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-900 mb-2 text-sm">
              Program Information
            </h3>
            <div className="space-y-1.5 text-xs">
              <p>
                <span className="font-medium text-gray-700">Institution:</span>{" "}
                <span className="text-gray-900">
                  {data.institution_details?.school_name || "N/A"}
                </span>
              </p>
              <p>
                <span className="font-medium text-gray-700">Program:</span>{" "}
                <span className="text-gray-900">
                  {data.programme_details?.title || "N/A"}
                </span>
              </p>
              <p>
                <span className="font-medium text-gray-700">Faculty:</span>{" "}
                <span className="text-gray-900">
                  {data.programme_details?.faculty || "N/A"}
                </span>
              </p>
              <p>
                <span className="font-medium text-gray-700">
                  Mode of Entry:
                </span>{" "}
                <span className="text-gray-900">
                  {data.mode_of_entry || "N/A"}
                </span>
              </p>
            </div>
          </div>

          <div
            className={`p-3 rounded-lg border ${
              isDisqualified
                ? "bg-red-50 border-red-200"
                : "bg-green-50 border-green-200"
            }`}
          >
            <h3
              className={`font-semibold mb-2 text-sm ${
                isDisqualified ? "text-red-700" : "text-green-700"
              }`}
            >
              Your Eligibility Status
            </h3>
            <div className="flex items-center space-x-2 mb-3">
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  isDisqualified
                    ? "bg-red-100 text-red-800"
                    : "bg-green-100 text-green-800"
                }`}
              >
                {eligibilityStatus}
              </span>
            </div>

            <div className="mb-3">
              <p className="font-medium text-gray-700 mb-1.5 text-xs">
                UTME Subjects Combination Result:
                <span
                  className={`ml-2 ${
                    data.utme_subjects_result?.toLowerCase() === "disqualified"
                      ? "text-red-600"
                      : "text-green-600"
                  }`}
                >
                  {data.utme_subjects_result || "N/A"}
                </span>
              </p>

              <div className="mt-1.5">
                <p className="text-xs font-medium text-gray-600 mb-1">
                  Required UTME Subjects:
                </p>
                <p className="text-xs text-gray-700 bg-white p-1.5 rounded">
                  {data.programme_details?.subjects || "Not specified"}
                </p>
              </div>
            </div>

            {data.user_input_utme && (
              <div>
                <p className="font-medium text-gray-700 mb-1.5 text-xs">
                  Your UTME Subjects:
                </p>
                <div className="space-y-1.5">
                  {data.user_input_utme.passed?.length > 0 && (
                    <div className="bg-white p-1.5 rounded">
                      <p className="text-[10px] text-green-600 font-medium mb-0.5">
                        ✓ Passed:
                      </p>
                      <ul className="list-disc pl-4 text-xs text-gray-700">
                        {data.user_input_utme.passed.map((subject, i) => (
                          <li key={i}>{subject}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {data.user_input_utme.failed?.length > 0 && (
                    <div className="bg-white p-1.5 rounded">
                      <p className="text-[10px] text-red-600 font-medium mb-0.5">
                        ✗ Failed/Not Matching:
                      </p>
                      <ul className="list-disc pl-4 text-xs text-gray-700">
                        {data.user_input_utme.failed.map((subject, i) => (
                          <li key={i}>{subject}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="h-full flex flex-col">
            {(data.user_input_olevel ||
              data.user_input_olevel_pass ||
              data.user_input_alevel ||
              data.user_input_alevel_pass) && (
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 p-3 rounded-lg border border-gray-200 lg:col-span-2 h-full">
                <h3 className="font-semibold text-gray-800 mb-2 text-sm">
                  Your Input Subjects
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(data.user_input_olevel || data.user_input_olevel_pass) && (
                    <div>
                      <p className="font-medium text-gray-700 mb-1 text-xs">
                        O'Level Subjects
                      </p>
                      <div className="space-y-1.5">
                        {data.user_input_olevel?.passed?.length > 0 && (
                          <div className="bg-white p-1.5 rounded">
                            <p className="text-[10px] text-green-600 font-medium mb-0.5">
                              ✓ Passed:
                            </p>
                            <ul className="list-disc pl-4 text-xs text-gray-700">
                              {data.user_input_olevel.passed.map(
                                (subject, i) => (
                                  <li key={i}>{subject}</li>
                                )
                              )}
                            </ul>
                          </div>
                        )}
                        {data.user_input_olevel?.failed?.length > 0 && (
                          <div className="bg-white p-1.5 rounded">
                            <p className="text-[10px] text-red-600 font-medium mb-0.5">
                              ✗ Failed/Not Matching:
                            </p>
                            <ul className="list-disc pl-4 text-xs text-gray-700">
                              {data.user_input_olevel.failed.map(
                                (subject, i) => (
                                  <li key={i}>{subject}</li>
                                )
                              )}
                            </ul>
                          </div>
                        )}
                        {data.user_input_olevel_pass?.passed?.length > 0 && (
                          <div className="bg-white p-1.5 rounded">
                            <p className="text-[10px] text-green-600 font-medium mb-0.5">
                              ✓ Pass Subjects:
                            </p>
                            <ul className="list-disc pl-4 text-xs text-gray-700">
                              {data.user_input_olevel_pass.passed.map(
                                (subject, i) => (
                                  <li key={i}>{subject}</li>
                                )
                              )}
                            </ul>
                          </div>
                        )}
                        {data.user_input_olevel_pass?.failed?.length > 0 && (
                          <div className="bg-white p-1.5 rounded">
                            <p className="text-[10px] text-red-600 font-medium mb-0.5">
                              ✗ Failed Pass Subjects:
                            </p>
                            <ul className="list-disc pl-4 text-xs text-gray-700">
                              {data.user_input_olevel_pass.failed.map(
                                (subject, i) => (
                                  <li key={i}>{subject}</li>
                                )
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {(data.user_input_alevel || data.user_input_alevel_pass) && (
                    <div>
                      <p className="font-medium text-gray-700 mb-1 text-xs">
                        A'Level Subjects
                      </p>
                      <div className="space-y-1.5">
                        {data.user_input_alevel?.passed?.length > 0 && (
                          <div className="bg-white p-1.5 rounded">
                            <p className="text-[10px] text-green-600 font-medium mb-0.5">
                              ✓ Passed:
                            </p>
                            <ul className="list-disc pl-4 text-xs text-gray-700">
                              {data.user_input_alevel.passed.map(
                                (subject, i) => (
                                  <li key={i}>{subject}</li>
                                )
                              )}
                            </ul>
                          </div>
                        )}
                        {data.user_input_alevel?.failed?.length > 0 && (
                          <div className="bg-white p-1.5 rounded">
                            <p className="text-[10px] text-red-600 font-medium mb-0.5">
                              ✗ Failed/Not Matching:
                            </p>
                            <ul className="list-disc pl-4 text-xs text-gray-700">
                              {data.user_input_alevel.failed.map(
                                (subject, i) => (
                                  <li key={i}>{subject}</li>
                                )
                              )}
                            </ul>
                          </div>
                        )}
                        {data.user_input_alevel_pass?.passed?.length > 0 && (
                          <div className="bg-white p-1.5 rounded">
                            <p className="text-[10px] text-green-600 font-medium mb-0.5">
                              ✓ Pass Subjects:
                            </p>
                            <ul className="list-disc pl-4 text-xs text-gray-700">
                              {data.user_input_alevel_pass.passed.map(
                                (subject, i) => (
                                  <li key={i}>{subject}</li>
                                )
                              )}
                            </ul>
                          </div>
                        )}
                        {data.user_input_alevel_pass?.failed?.length > 0 && (
                          <div className="bg-white p-1.5 rounded">
                            <p className="text-[10px] text-red-600 font-medium mb-0.5">
                              ✗ Failed Pass Subjects:
                            </p>
                            <ul className="list-disc pl-4 text-xs text-gray-700">
                              {data.user_input_alevel_pass.failed.map(
                                (subject, i) => (
                                  <li key={i}>{subject}</li>
                                )
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
            <h3 className="font-semibold text-gray-800 mb-2 text-sm">
              Program Requirements
            </h3>

            {data.programme_utme_subject_data && (
              <div className="mb-2">
                <p className="text-xs font-medium text-gray-700 mb-1">
                  UTME Subject Requirements:
                </p>
                <div className="text-xs text-gray-600 bg-white p-2 rounded prose prose-sm max-w-none">
                  {data.programme_utme_subject_data.title}
                </div>
              </div>
            )}

            {data.programme_utme_requirements_data && (
              <div className="mb-2">
                <p className="text-xs font-medium text-gray-700 mb-1">
                  UTME Requirements:
                </p>
                <div className="text-xs text-gray-600 bg-white p-2 rounded prose prose-sm max-w-none">
                  {data.programme_utme_requirements_data.title}
                </div>
              </div>
            )}

            {data.programme_de_requirements_data && (
              <div>
                <p className="text-xs font-medium text-gray-700 mb-1">
                  Direct Entry Requirements:
                </p>
                <div className="text-xs text-gray-600 bg-white p-2 rounded prose prose-sm max-w-none">
                  {data.programme_de_requirements_data.title}
                </div>
              </div>
            )}
          </div>

          {data.alternatives && data.alternatives.length > 0 && (
            <div className="bg-amber-50 p-3 rounded-lg border border-amber-200 lg:col-span-2">
              <h3 className="font-semibold text-amber-800 mb-2 text-sm">
                ALTERNATIVES ({data.alternatives.length} program(s) available)
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full bg-white rounded text-xs">
                  <thead>
                    <tr className="bg-gray-200">
                      <th className="px-2 py-1.5 text-left font-semibold text-gray-700">
                        Programme
                      </th>
                      <th className="px-2 py-1.5 text-left font-semibold text-gray-700">
                        Inst.
                      </th>
                      <th className="px-2 py-1.5 text-left font-semibold text-gray-700">
                        UTME Subjects
                      </th>
                      <th className="px-2 py-1.5 text-left font-semibold text-gray-700">
                        Requirements
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.alternatives.map((alt, index) => (
                      <tr
                        key={index}
                        className="border-b border-gray-200 hover:bg-gray-50"
                      >
                        <td className="px-2 py-2 align-top">
                          {alt.course_name}
                        </td>
                        <td className="px-2 py-2 align-top">
                          {alt.institution_name}
                        </td>
                        <td className="px-2 py-2 align-top">{alt.title}</td>
                        <td className="px-2 py-2 align-top">
                          <div className="mb-1">
                            <span className="font-semibold">
                              UTME Requirements
                            </span>
                            <div
                              className="text-gray-600 mt-0.5"
                              dangerouslySetInnerHTML={{
                                __html: alt.utme_requirements,
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end mt-4 pt-3 border-t border-gray-200 sticky bottom-0 bg-white space-x-3 print:hidden">
          <button
            onClick={handlePrint}
            className="flex items-center px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-xs font-medium hover:bg-gray-100 transition shadow-sm"
          >
            <FaPrint className="mr-1.5 text-sm" />
            Print
          </button>

          <button
            onClick={() => setModal(false)}
            className="flex items-center px-4 py-2 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition shadow-sm"
          >
            Close
            <IoCloseOutline className="ml-1.5 text-base" />
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default EligibilityModal;
